import type { FoldFile, FoldEdgeAssignment } from './types';

export interface FoldValidationIssue {
  severity: 'error' | 'warning';
  message: string;
}

const VALID_ASSIGNMENTS = new Set<FoldEdgeAssignment>(['B', 'M', 'V', 'F', 'U', 'C', 'J']);

/**
 * Semantic validation of a resolved FOLD frame. Errors mean the frame cannot be
 * turned into a crease pattern; warnings mean we repaired something.
 */
export function validateFold(frame: FoldFile): FoldValidationIssue[] {
  const issues: FoldValidationIssue[] = [];
  const coords = frame.vertices_coords;
  const edges = frame.edges_vertices;

  if (!coords || coords.length === 0) {
    issues.push({ severity: 'error', message: 'The frame has no vertices_coords.' });
    return issues;
  }
  for (let i = 0; i < coords.length; i++) {
    const c = coords[i];
    if (!Array.isArray(c) || c.length < 2 || !c.slice(0, 2).every(Number.isFinite)) {
      issues.push({
        severity: 'error',
        message: `vertices_coords[${i}] is not a finite 2D or 3D point.`,
      });
      return issues;
    }
    if (c.length > 2 && Number.isFinite(c[2]) && Math.abs(c[2]) > 1e-9) {
      issues.push({
        severity: 'warning',
        message: `Vertex ${i} has a non-zero z coordinate; fold.app treats the imported frame as a flat crease pattern and drops z.`,
      });
      break;
    }
  }

  if (!edges || edges.length === 0) {
    issues.push({ severity: 'error', message: 'The frame has no edges_vertices.' });
    return issues;
  }
  for (let e = 0; e < edges.length; e++) {
    const pair = edges[e];
    if (!Array.isArray(pair) || pair.length !== 2) {
      issues.push({ severity: 'error', message: `edges_vertices[${e}] is not a pair.` });
      return issues;
    }
    for (const v of pair) {
      if (!Number.isInteger(v) || v < 0 || v >= coords.length) {
        issues.push({
          severity: 'error',
          message: `edges_vertices[${e}] refers to vertex ${v}, which does not exist.`,
        });
        return issues;
      }
    }
    if (pair[0] === pair[1]) {
      issues.push({
        severity: 'warning',
        message: `Edge ${e} is a self-loop and will be dropped.`,
      });
    }
  }

  const assignment = frame.edges_assignment;
  if (assignment) {
    if (assignment.length !== edges.length) {
      issues.push({
        severity: 'warning',
        message: `edges_assignment has ${assignment.length} entries for ${edges.length} edges; missing entries default to "U".`,
      });
    }
    for (let e = 0; e < assignment.length; e++) {
      if (!VALID_ASSIGNMENTS.has(assignment[e])) {
        issues.push({
          severity: 'warning',
          message: `Edge ${e} has unknown assignment "${assignment[e]}"; treating it as unassigned.`,
        });
      }
    }
  } else {
    issues.push({
      severity: 'warning',
      message: 'No edges_assignment; assignments will be inferred from edges_foldAngle where available.',
    });
  }

  const angles = frame.edges_foldAngle;
  if (angles && angles.length !== edges.length) {
    issues.push({
      severity: 'warning',
      message: `edges_foldAngle has ${angles.length} entries for ${edges.length} edges.`,
    });
  }
  if (angles) {
    for (let e = 0; e < angles.length; e++) {
      const a = angles[e];
      if (a === null || a === undefined) continue;
      if (!Number.isFinite(a) || Math.abs(a) > 180.0001) {
        issues.push({
          severity: 'warning',
          message: `edges_foldAngle[${e}] = ${a} is outside [-180, 180] degrees; it will be clamped.`,
        });
      }
    }
  }

  if (frame.faces_vertices) {
    for (let f = 0; f < frame.faces_vertices.length; f++) {
      const loop = frame.faces_vertices[f];
      if (!Array.isArray(loop) || loop.length < 3) {
        issues.push({
          severity: 'warning',
          message: `faces_vertices[${f}] has fewer than 3 vertices; faces will be re-derived from the edges.`,
        });
        break;
      }
    }
  }

  return issues;
}
